/**
 * PowerLogPage Component
 * Main page for power log management - daily transaction tracking for bail bond agents
 * Uses Oath authentication and OathUI components for consistent Tailwind styling
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../services/oathAuthContext';
import usePermissions from '../../../hooks/usePermissions';
import {
  getPowerLogs,
  deletePowerLog,
  exportPowerLogs,
  DEFAULT_PAGE_SIZE,
  DEBOUNCE_DELAY_MS
} from '../../../services/oathPowerLogService';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathDualSpinner,
  OathEmptyState,
  OathAgencySelector,
  OathAlert
} from '../../shared/OathUI';
import PowerLogTable from './components/PowerLogTable';
import PowerLogCard from './components/PowerLogCard';
import AddEditPowerLogModal from './modals/AddEditPowerLogModal';
import { useIsMobile, useDebounce } from './utils/hooks';
import { formatDate, formatDateForInput } from './utils/formatters';
import { withErrorBoundary, TableLoadingSkeleton, Pagination } from './components/shared';

const PowerLogPage = () => {
  const { user, isLoading: authLoading } = useOathAuth();
  const { canAccessPowerReporting } = usePermissions();
  const isMobile = useIsMobile();

  // Agency selection for multi-agency users
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);

  // Data state
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Consolidated filter state — one object, one debounce
  const INITIAL_FILTERS = {
    fromDate: null, toDate: null,
    searchPowerNo: '', searchDefendantName: '',
    searchCaseNumber: '', searchDisposition: '',
    searchAgentName: '', searchCollateralReceiptNo: '',
    searchCollateral: '',
    dispositionFromDate: null, dispositionToDate: null,
    minAmountOfBond: '', maxAmountOfBond: '',
    minPremiumCharged: '', maxPremiumCharged: '',
    minCompanyPremium: '', maxCompanyPremium: '',
  };
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const debouncedFilters = useDebounce(filters, DEBOUNCE_DELAY_MS);
  const [showFilters, setShowFilters] = useState(false);

  // Helper for updating individual filter fields
  const updateFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Modal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingLog, setDeletingLog] = useState(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Track if initial load has happened
  const initialLoadRef = useRef(false);

  // AbortController for request cancellation
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Set default agency on mount - prefer main agency, then canPost, then first
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      // Find main agency first, then fall back to one with posting privileges, then first agency
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Load power logs function (not a useCallback to avoid dependency issues)
  const loadLogs = async (resetPage = false) => {
    if (!selectedAgencyId) return;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const currentRequestId = ++requestIdRef.current;

    const currentPage = resetPage ? 0 : page;
    if (resetPage && page !== 0) {
      setPage(0);
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await getPowerLogs({
        agencyId: selectedAgencyId,
        pageNumber: currentPage + 1,
        pageSize,
        ...debouncedFilters,
        signal: abortController.signal
      });

      if (currentRequestId !== requestIdRef.current) return;

      if (result.success && result.data) {
        const items = result.data.items || [];
        let count = result.data.totalCount || 0;

        // Defensive: if server returns fewer items than a full page
        // but totalCount says there are more, the count is wrong.
        // Correct it to: items already seen on prior pages + items on this page.
        if (items.length < pageSize && (currentPage * pageSize + items.length) < count) {
          count = currentPage * pageSize + items.length;
        }

        setLogs(items);
        setTotalCount(count);
      } else {
        console.error('Failed to fetch power logs:', result.error);
        const msg = result.error || 'Failed to load power logs';
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (currentRequestId !== requestIdRef.current) return;
      console.error('Failed to fetch power logs:', err);
      setError('Failed to load power logs');
      toast.error('Failed to load power logs');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load on initial mount and when filters or agency change
  useEffect(() => {
    if (!selectedAgencyId) return;

    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadLogs();
      return;
    }

    loadLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgencyId, debouncedFilters]);

  // Load when page or pageSize changes (not filter changes)
  useEffect(() => {
    if (!selectedAgencyId || !initialLoadRef.current) return;
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Handle agency change
  const handleAgencyChange = (agencyId) => {
    setSelectedAgencyId(agencyId);
    setPage(0);
    setFilters(INITIAL_FILTERS);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  // Handle add new log
  const handleAddNew = () => {
    setEditingLog(null);
    setShowAddEditModal(true);
  };

  // Handle edit log
  const handleEdit = (log) => {
    setEditingLog(log);
    setShowAddEditModal(true);
  };

  // Handle delete log
  const handleDeleteClick = (log) => {
    setDeletingLog(log);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingLog) return;

    try {
      const result = await deletePowerLog(deletingLog.logId, selectedAgencyId);
      if (result.success) {
        toast.success('Power log entry deleted successfully');
        loadLogs();
      } else {
        toast.error(result.error || 'Failed to delete power log entry');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete power log entry');
    } finally {
      setShowDeleteConfirm(false);
      setDeletingLog(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingLog(null);
  };

  // Handle save (add/edit)
  const handleSave = () => {
    setShowAddEditModal(false);
    setEditingLog(null);
    loadLogs();
  };

  // Handle refresh (used by Save + New to refresh list without closing modal)
  const handleRefresh = () => {
    loadLogs();
  };

  // Handle export
  const handleExport = async () => {
    if (!selectedAgencyId) return;

    setIsExporting(true);
    try {
      const result = await exportPowerLogs({
        agencyId: selectedAgencyId,
        ...debouncedFilters
      }, true);

      if (result.success) {
        // Show warning if export was limited
        if (result.message) {
          toast.warning(result.message, { autoClose: 8000 });
        } else {
          toast.success('Power log exported successfully');
        }
      } else {
        toast.error(result.error || 'Failed to export power logs');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export power logs');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(0);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(v => v !== null && v !== '');

  // Count active "more filters" (excludes power # and defendant name which are always visible)
  const moreFiltersCount = [
    filters.fromDate, filters.toDate, filters.searchCaseNumber, filters.searchDisposition,
    filters.searchAgentName, filters.searchCollateralReceiptNo, filters.searchCollateral,
    filters.dispositionFromDate, filters.dispositionToDate,
    filters.minAmountOfBond, filters.maxAmountOfBond, filters.minPremiumCharged,
    filters.maxPremiumCharged, filters.minCompanyPremium, filters.maxCompanyPremium
  ].filter(Boolean).length;

  // Loading state
  if (authLoading) {
    return (
      <OathPageWrapper>
        <div className="flex items-center justify-center min-h-[400px]">
          <OathDualSpinner size="lg" />
        </div>
      </OathPageWrapper>
    );
  }

  return (
    <OathPageWrapper>
      {/* Header */}
      <OathHeaderCard
        icon={ClipboardDocumentListIcon}
        title="Power Log"
        subtitle="Track your daily bond transactions"
      >
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          {agencies.length > 1 && (
            <OathAgencySelector
              agencies={agencies}
              selectedAgencyId={selectedAgencyId}
              onSelect={handleAgencyChange}
            />
          )}
          <OathButton onClick={handleAddNew} variant="primary">
            <PlusIcon className="h-5 w-5 sm:mr-1" />
            <span className="hidden sm:inline">Add Entry</span>
          </OathButton>
        </div>
      </OathHeaderCard>

      {/* Filters */}
      <OathContentCard className="mb-6">
        <div className="space-y-4">
          {/* Search bar row */}
          <div className="flex flex-wrap gap-4 items-end">
            {/* Power Number Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Power Number
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchPowerNo}
                  onChange={(e) => updateFilter('searchPowerNo', e.target.value)}
                  placeholder="Search by power number..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Defendant Name Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant Name
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchDefendantName}
                  onChange={(e) => updateFilter('searchDefendantName', e.target.value)}
                  placeholder="Search by name..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Toggle additional filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="relative px-4 py-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
            >
              {showFilters ? 'Hide Filters' : 'More Filters'}
              {!showFilters && moreFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-teal-500 rounded-full">
                  {moreFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Additional filters (collapsible) */}
          {showFilters && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">

              {/* Case Details */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Case Details</h4>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Case Number</label>
                    <input
                      type="text"
                      value={filters.searchCaseNumber}
                      onChange={(e) => updateFilter('searchCaseNumber', e.target.value)}
                      placeholder="Search case number..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disposition</label>
                    <input
                      type="text"
                      value={filters.searchDisposition}
                      onChange={(e) => updateFilter('searchDisposition', e.target.value)}
                      placeholder="Search disposition..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Dates</h4>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Executed From</label>
                    <input
                      type="date"
                      value={formatDateForInput(filters.fromDate)}
                      onChange={(e) => updateFilter('fromDate', e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Executed To</label>
                    <input
                      type="date"
                      value={formatDateForInput(filters.toDate)}
                      onChange={(e) => updateFilter('toDate', e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disposition Date From</label>
                    <input
                      type="date"
                      value={formatDateForInput(filters.dispositionFromDate)}
                      onChange={(e) => updateFilter('dispositionFromDate', e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disposition Date To</label>
                    <input
                      type="date"
                      value={formatDateForInput(filters.dispositionToDate)}
                      onChange={(e) => updateFilter('dispositionToDate', e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Financial */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Financial</h4>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bond Amount Min</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.minAmountOfBond}
                      onChange={(e) => updateFilter('minAmountOfBond', e.target.value)}
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bond Amount Max</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.maxAmountOfBond}
                      onChange={(e) => updateFilter('maxAmountOfBond', e.target.value)}
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Premium Min</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.minPremiumCharged}
                      onChange={(e) => updateFilter('minPremiumCharged', e.target.value)}
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Premium Max</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.maxPremiumCharged}
                      onChange={(e) => updateFilter('maxPremiumCharged', e.target.value)}
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Co. Premium Min</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.minCompanyPremium}
                      onChange={(e) => updateFilter('minCompanyPremium', e.target.value)}
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Co. Premium Max</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filters.maxCompanyPremium}
                      onChange={(e) => updateFilter('maxCompanyPremium', e.target.value)}
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Other */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Other</h4>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agent Name</label>
                    <input
                      type="text"
                      value={filters.searchAgentName}
                      onChange={(e) => updateFilter('searchAgentName', e.target.value)}
                      placeholder="Search agent name..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Collateral Receipt #</label>
                    <input
                      type="text"
                      value={filters.searchCollateralReceiptNo}
                      onChange={(e) => updateFilter('searchCollateralReceiptNo', e.target.value)}
                      placeholder="Search receipt #..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Collateral Details</label>
                    <input
                      type="text"
                      value={filters.searchCollateral}
                      onChange={(e) => updateFilter('searchCollateral', e.target.value)}
                      placeholder="Search collateral..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <div className="pt-2">
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <XMarkIcon className="h-5 w-5 mr-1" />
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Actions row */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isLoading ? (
                'Loading...'
              ) : (
                `Showing ${logs.length} of ${totalCount} entries`
              )}
            </div>
            <OathButton
              onClick={handleExport}
              variant="secondary"
              disabled={isExporting || totalCount === 0}
            >
              {isExporting ? (
                <>
                  <OathDualSpinner size="sm" className="mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                  Export CSV
                </>
              )}
            </OathButton>
          </div>
        </div>
      </OathContentCard>

      {/* Content */}
      <OathContentCard>
        {error && !isLoading && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <p className="text-red-800 dark:text-red-200 mb-3">{error}</p>
            <button
              onClick={() => loadLogs()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md shadow-sm transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
        {isLoading && logs.length === 0 ? (
          <TableLoadingSkeleton rows={5} />
        ) : logs.length === 0 && !error ? (
          <OathEmptyState
            icon={ClipboardDocumentListIcon}
            title="No power log entries"
            description={hasActiveFilters
              ? "No entries match your search criteria. Try adjusting your filters."
              : "You haven't added any power log entries yet. Click 'Add Entry' to get started."
            }
          />
        ) : (
          <div className={isLoading && logs.length > 0 ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
            {isMobile ? (
              // Mobile card view
              <div className="space-y-4">
                {logs.map((log) => (
                  <PowerLogCard
                    key={log.logId}
                    log={log}
                    onEdit={() => handleEdit(log)}
                    onDelete={() => handleDeleteClick(log)}
                  />
                ))}
              </div>
            ) : (
              // Desktop table view
              <PowerLogTable
                logs={logs}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            )}
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="mt-6">
            <Pagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        )}
      </OathContentCard>

      {/* Add/Edit Modal */}
      {showAddEditModal && (
        <AddEditPowerLogModal
          isOpen={showAddEditModal}
          onClose={() => {
            setShowAddEditModal(false);
            setEditingLog(null);
          }}
          log={editingLog}
          agencyId={selectedAgencyId}
          onSave={handleSave}
          onRefresh={handleRefresh}
          showReportAssignment={canAccessPowerReporting}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Power Log Entry?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the log entry for power{' '}
              <span className="font-semibold">{deletingLog?.powerNo}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <OathButton variant="secondary" onClick={handleDeleteCancel}>
                Cancel
              </OathButton>
              <OathButton variant="danger" onClick={handleDeleteConfirm}>
                Delete
              </OathButton>
            </div>
          </div>
        </div>
      )}

    </OathPageWrapper>
  );
};

export default withErrorBoundary(PowerLogPage);
